import { css } from 'styled-components';

import type { TranslatedElement } from '@src/components/transcript/hooks/useTranslatedElements';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Represents a search match with all element indices that are part of it
 */
export type SearchMatch = {
  /** First element index in this match (used for navigation) */
  startIndex: number;
  /** All element indices that should be highlighted for this match */
  indices: number[];
};

/**
 * Find all transcript element indices whose value contains the search term.
 * Skips removed/cut elements. Case-insensitive substring match.
 * Supports multi-word search by concatenating adjacent elements.
 *
 * @returns Array of SearchMatch objects, each representing a distinct match occurrence
 */
export function findTranscriptMatchesWithRanges(
  elements: TranscriptElement[],
  query: string,
  /** When true, also search removed/cut elements (e.g., redline view shows all) */
  includeRemovedElements = false,
): SearchMatch[] {
  if (!query || !elements.length) return [];

  // Normalize whitespace: trim and collapse multiple spaces into single space
  const normalizedQuery = query.toLowerCase().trim().replace(/\s+/g, ' ');
  if (!normalizedQuery) return [];

  const matches: SearchMatch[] = [];

  // Single-word search: check each element individually
  if (!normalizedQuery.includes(' ')) {
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (
        !includeRemovedElements &&
        (el.state === 'removed' || el.state === 'cut')
      )
        continue;
      if (el.value && el.value.toLowerCase().includes(normalizedQuery)) {
        matches.push({ startIndex: i, indices: [i] });
      }
    }
    return matches;
  }

  // Multi-word search: find distinct phrase matches using word-by-word comparison
  // Supports partial matching of the last word for better UX
  const queryWords = normalizedQuery.split(' ');
  const maxWordsToCollect = queryWords.length + 5; // collect extra words for partial matching

  for (let start = 0; start < elements.length; start++) {
    const startEl = elements[start];
    if (
      !includeRemovedElements &&
      (startEl.state === 'removed' || startEl.state === 'cut')
    )
      continue;

    // Collect words and indices starting from current position
    const candidateWords: string[] = [];
    const candidateIndices: number[] = [];

    for (
      let j = start;
      j < elements.length && candidateWords.length < maxWordsToCollect;
      j++
    ) {
      const el = elements[j];
      if (
        !includeRemovedElements &&
        (el.state === 'removed' || el.state === 'cut')
      )
        continue;

      if (el.value) {
        // Normalize the word: lowercase and collapse internal whitespace
        const normalizedWord = el.value
          .toLowerCase()
          .trim()
          .replace(/\s+/g, ' ');
        if (normalizedWord) {
          candidateWords.push(normalizedWord);
          candidateIndices.push(j);
        }
      }
    }

    // Try to find a match starting at this position
    // We check if the concatenated phrase starts with our query (for partial last word)
    if (candidateWords.length >= queryWords.length) {
      // Build phrase from exactly the number of query words
      const candidatePhrase = candidateWords
        .slice(0, queryWords.length)
        .join(' ');

      // Check for exact match or if candidatePhrase starts with query (partial last word)
      if (
        candidatePhrase === normalizedQuery ||
        candidatePhrase.startsWith(normalizedQuery + ' ')
      ) {
        const matchIndices = candidateIndices.slice(0, queryWords.length);
        matches.push({
          startIndex: matchIndices[0],
          indices: matchIndices,
        });
        // Skip ahead to avoid overlapping matches
        start += queryWords.length - 1; // -1 because loop will increment
        continue;
      }

      // Also check if we have a partial match on the last word
      // e.g., searching "things th" should match "things that"
      if (candidateWords.length > 0) {
        const phraseWithPartialLast = candidateWords
          .slice(0, queryWords.length)
          .join(' ');
        if (phraseWithPartialLast.startsWith(normalizedQuery)) {
          const matchIndices = candidateIndices.slice(0, queryWords.length);
          matches.push({
            startIndex: matchIndices[0],
            indices: matchIndices,
          });
          // Skip ahead to avoid overlapping matches
          start += queryWords.length - 1; // -1 because loop will increment
          continue;
        }
      }
    }
  }

  return matches;
}

/**
 * Legacy wrapper that returns flat array of start indices for backward compatibility
 */
export function findTranscriptMatches(
  elements: TranscriptElement[],
  query: string,
  includeRemovedElements = false,
): number[] {
  return findTranscriptMatchesWithRanges(
    elements,
    query,
    includeRemovedElements,
  ).map(m => m.startIndex);
}

export const SEARCH_MATCH_CLASS = 'search-match';
export const SEARCH_MATCH_ACTIVE_CLASS = 'search-match-active';

/** Shared styled-components CSS for search highlight classes.
 *  Handles both direct matches (`&.search-match`) and child matches (`& .search-match`). */
export const searchHighlightStyles = css`
  &.${SEARCH_MATCH_CLASS}, & .${SEARCH_MATCH_CLASS} {
    background-color: rgba(255, 200, 0, 0.3);
  }
  &.${SEARCH_MATCH_ACTIVE_CLASS}, & .${SEARCH_MATCH_ACTIVE_CLASS} {
    background-color: rgba(255, 200, 0, 0.7);
    color: black;
  }
`;

/**
 * Remove all search highlight classes from word spans within a container.
 */
export function clearSearchHighlights(container: HTMLElement): void {
  container
    .querySelectorAll(`.${SEARCH_MATCH_CLASS}`)
    .forEach(el => el.classList.remove(SEARCH_MATCH_CLASS));
  container
    .querySelectorAll(`.${SEARCH_MATCH_ACTIVE_CLASS}`)
    .forEach(el => el.classList.remove(SEARCH_MATCH_ACTIVE_CLASS));
}

/**
 * Apply search highlight classes to word spans matching the given matches.
 * The active match receives an additional class and is scrolled into view.
 *
 * @param container - The scrollable transcript DOM container
 * @param matches - Array of SearchMatch objects with indices to highlight
 * @param activeIdx - Index into matches for the currently focused result
 */
export function applySearchHighlights(
  container: HTMLElement,
  matches: SearchMatch[],
  activeIdx: number,
  /** The searched elements. Needed to locate a word inside a redline block
   *  span; without them the redline falls back to the per-word path. */
  elements?: TranscriptElement[],
): void {
  if (
    elements &&
    applyRedlineSearchHighlights(container, matches, activeIdx, elements)
  ) {
    return;
  }

  // Highlight all words in all matches
  for (const match of matches) {
    for (const idx of match.indices) {
      const el = container.querySelector(`[data-index='${idx}']`);
      if (el) el.classList.add(SEARCH_MATCH_CLASS);
    }
  }

  // Apply active class to all words in the active match
  if (matches.length > 0 && activeIdx < matches.length) {
    const activeMatch = matches[activeIdx];
    for (const idx of activeMatch.indices) {
      const el = container.querySelector(`[data-index='${idx}']`);
      if (el) el.classList.add(SEARCH_MATCH_ACTIVE_CLASS);
    }

    // Scroll to the first element of the active match
    const firstEl = container.querySelector(
      `[data-index='${activeMatch.startIndex}']`,
    );
    if (firstEl) {
      firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

/**
 * Move the active highlight from one match to another.
 * Removes the active class from the current match and applies it to the new one.
 *
 * @returns The DOM element of the newly active match, or null
 */
export function moveActiveHighlight(
  container: HTMLElement,
  matches: SearchMatch[],
  fromIdx: number,
  toIdx: number,
): HTMLElement | null {
  // Remove active class from all words in current match
  if (fromIdx >= 0 && fromIdx < matches.length) {
    const currentMatch = matches[fromIdx];
    for (const idx of currentMatch.indices) {
      const el = container.querySelector(`[data-index='${idx}']`);
      if (el) el.classList.remove(SEARCH_MATCH_ACTIVE_CLASS);
    }
  }

  // Apply active class to all words in new match
  if (toIdx >= 0 && toIdx < matches.length) {
    const nextMatch = matches[toIdx];
    for (const idx of nextMatch.indices) {
      const el = container.querySelector(`[data-index='${idx}']`);
      if (el) el.classList.add(SEARCH_MATCH_ACTIVE_CLASS);
    }

    // Scroll to first element of new match
    const firstEl = container.querySelector<HTMLElement>(
      `[data-index='${nextMatch.startIndex}']`,
    );
    if (firstEl) {
      firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return firstEl;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Translated-language search
// ---------------------------------------------------------------------------
//
// Translated transcripts render text from `phrase.translatedValue` (or the
// element's own `value` for unphrased elements) inside a single `<Word>` span
// keyed by `data-initial-index`. The original-language search (above) keys
// matches by `data-index` and highlights whole word-spans, which is wrong on
// two axes for the translated case:
//   1. The translated DOM is `data-initial-index`, not `data-index`.
//   2. We want character-level highlighting *inside* a phrase span, not
//      whole-phrase highlighting — a phrase can be a long sentence.
//
// We solve (2) with the CSS Custom Highlight API (`CSS.highlights`). Each
// match becomes a `Range` over the matched substring within the phrase's
// text node; the browser paints the highlight color over those ranges. No
// DOM mutation, no text-node splitting — so React-controlled rendering
// stays intact and re-renders never wipe the highlight. The only catch is
// browser support: Chrome 105+, Safari 17.2+, Firefox 140+. Older browsers
// silently skip translated highlights (the search bar's match count still
// works correctly, just no visual highlight).

/** A translated-text match. References its phrase span via initial_index
 * and identifies the substring by (charOffset, length). */
export type TranslatedSearchMatch = {
  /** initial_index of the phrase lead element — used to locate the DOM span. */
  phraseInitialIndex: number;
  /**
   * PLAT-10611 (F5) — the phrase lead element's stable UUID (`source_uuid ?? id`),
   * the value the translated span stamps on `data-source-uuid`. The highlighter
   * locates the span by this id FIRST (drift-correct), falling back to
   * `phraseInitialIndex`. Absent when the element carries no id.
   */
  phraseUuid?: string;
  /** Character offset within the phrase's translated text where the match starts. */
  charOffset: number;
  /** Length of the matched text (in code units). */
  matchLength: number;
};

const TRANSLATED_HIGHLIGHT_NAME = 'transcript-search-match';
const TRANSLATED_HIGHLIGHT_ACTIVE_NAME = 'transcript-search-match-active';

/** Global stylesheet rule for the translated-search highlight pseudo-elements.
 *  Applied via styled-components' `createGlobalStyle` so the `::highlight()`
 *  rules live at the document level (the pseudo-element targets ranges, not
 *  scoped DOM nodes). Color values mirror the original-language
 *  `searchHighlightStyles` to keep visual treatment consistent across languages. */
export const translatedSearchHighlightCss = `
  ::highlight(${TRANSLATED_HIGHLIGHT_NAME}) {
    background-color: rgba(255, 200, 0, 0.3);
  }
  ::highlight(${TRANSLATED_HIGHLIGHT_ACTIVE_NAME}) {
    background-color: rgba(255, 200, 0, 0.7);
    color: black;
  }
`;

/** Substring search across translated phrases. Iterates phrase leads and
 *  unphrased elements (where `isFirstInPhrase=true && displayValue` is the
 *  rendered text), case-insensitive. Multi-occurrence per phrase supported. */
export function findTranslatedMatches(
  translatedElements: TranslatedElement[],
  query: string,
  includeRemovedElements = false,
): TranslatedSearchMatch[] {
  if (!query || !translatedElements.length) return [];
  const normalized = query.toLowerCase().trim();
  if (!normalized) return [];

  const matches: TranslatedSearchMatch[] = [];
  for (const t of translatedElements) {
    // Only entries where text is rendered: phrase leads (displayValue =
    // translated phrase text) and unphrased text/punct (displayValue =
    // source value). Non-lead phrase members render `''` and don't carry
    // the phrase's text in DOM.
    if (!t.isFirstInPhrase) continue;
    const text = t.displayValue;
    if (!text) continue;

    if (
      !includeRemovedElements &&
      (t.element.state === 'removed' || t.element.state === 'cut')
    ) {
      continue;
    }

    const lowerText = text.toLowerCase();
    let idx = lowerText.indexOf(normalized);
    while (idx !== -1) {
      matches.push({
        phraseInitialIndex: t.element.initial_index,
        // PLAT-10611 (F5, R12) — carry the lead element's stable id for the
        // id-first span lookup.
        phraseUuid: t.element.source_uuid ?? t.element.id,
        charOffset: idx,
        matchLength: normalized.length,
      });
      // Advance past this match's start to find subsequent occurrences.
      // Using `idx + 1` rather than `idx + matchLength` lets overlapping
      // matches register, which matches the loose "find all occurrences"
      // semantics users typically expect from in-page search.
      idx = lowerText.indexOf(normalized, idx + 1);
    }
  }
  return matches;
}

// CSS Custom Highlight API — not yet in lib.dom.d.ts in many versions, so
// declare the minimum shape we use. Cast through this type at call sites.
type HighlightCtor = new (...ranges: Range[]) => HighlightLike;
type HighlightLike = { clear(): void };
type HighlightRegistry = {
  set(name: string, highlight: HighlightLike): void;
  delete(name: string): void;
};
type CssWithHighlights = typeof CSS & { highlights: HighlightRegistry };
type WindowWithHighlight = Window & { Highlight: HighlightCtor };

/** Whether the runtime supports the CSS Custom Highlight API. */
function supportsHighlightApi(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    'highlights' in (CSS as unknown as Record<string, unknown>) &&
    typeof (window as unknown as { Highlight?: unknown }).Highlight ===
      'function'
  );
}

/** Walk the span's descendants for the text node that contains the given
 *  character offset. Returns the text node and the offset within it.
 *  Translated phrase Words typically have a single text-node child, but we
 *  walk in case future markup adds wrapper elements. */
function findTextNodeAtCharOffset(
  span: HTMLElement,
  charOffset: number,
): { node: Text; offset: number } | null {
  const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT);
  let consumed = 0;
  let node = walker.nextNode() as Text | null;
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (consumed + len >= charOffset) {
      return { node, offset: charOffset - consumed };
    }
    consumed += len;
    node = walker.nextNode() as Text | null;
  }
  return null;
}

/** Clear any in-flight translated search highlights. Idempotent. */
export function clearTranslatedHighlights(): void {
  if (!supportsHighlightApi()) return;
  const cssH = CSS as unknown as CssWithHighlights;
  cssH.highlights.delete(TRANSLATED_HIGHLIGHT_NAME);
  cssH.highlights.delete(TRANSLATED_HIGHLIGHT_ACTIVE_NAME);
}

/** Paint translated search matches via the CSS Custom Highlight API.
 *  The active match is registered under a separate highlight name so its
 *  styling can differ. Also scrolls the active match's phrase span into view. */
export function applyTranslatedHighlights(
  container: HTMLElement,
  matches: TranslatedSearchMatch[],
  activeIdx: number,
): void {
  if (!supportsHighlightApi()) return;
  clearTranslatedHighlights();
  if (matches.length === 0) return;

  const matchRanges: Range[] = [];
  const activeRanges: Range[] = [];
  let activeSpan: HTMLElement | null = null;

  matches.forEach((m, i) => {
    // PLAT-10611 (F5, R12) — locate the phrase span by its stable id FIRST
    // (drift-correct), fall back to the retained `data-initial-index` (§6).
    const span =
      (m.phraseUuid
        ? container.querySelector<HTMLElement>(
            `[data-source-uuid='${m.phraseUuid}']`,
          )
        : null) ??
      container.querySelector<HTMLElement>(
        `[data-initial-index='${m.phraseInitialIndex}']`,
      );
    if (!span) return;
    const start = findTextNodeAtCharOffset(span, m.charOffset);
    if (!start) return;
    const end = findTextNodeAtCharOffset(span, m.charOffset + m.matchLength);
    // Silent-drop policy: if the match's end can't be located (the span's
    // total text content is shorter than `charOffset + matchLength` —
    // shouldn't happen if `findTranslatedMatches` was the source of the
    // ranges, but could happen if the DOM was mutated between match
    // computation and highlight application), skip this match. The match
    // count above the search bar still includes it; only the visual
    // highlight is dropped. Logging here would spam on every keystroke
    // for any phrase that wraps text across nodes, so we stay quiet.
    if (!end) return;

    let range: Range;
    try {
      range = new Range();
      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
    } catch {
      // `setStart` / `setEnd` throw on invalid offsets. Same silent-drop
      // policy as above — skip this range; the match count is still
      // accurate.
      return;
    }

    if (i === activeIdx) {
      activeRanges.push(range);
      activeSpan = span;
    } else {
      matchRanges.push(range);
    }
  });

  const cssH = CSS as unknown as CssWithHighlights;
  const Highlight = (window as unknown as WindowWithHighlight).Highlight;
  if (matchRanges.length > 0) {
    cssH.highlights.set(
      TRANSLATED_HIGHLIGHT_NAME,
      new Highlight(...matchRanges),
    );
  }
  if (activeRanges.length > 0) {
    cssH.highlights.set(
      TRANSLATED_HIGHLIGHT_ACTIVE_NAME,
      new Highlight(...activeRanges),
    );
  }
  if (activeSpan !== null) {
    (activeSpan as HTMLElement).scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}

// ---------------------------------------------------------------------------
// Redline (diff) search
// ---------------------------------------------------------------------------
//
// The redline view renders runs of same-state words as ONE span tagged
// `data-index={first}` / `data-index-end={last + 1}`, so most words have no
// `[data-index]` node of their own. And the regular view stays mounted under
// `display: none` beside it, so a plain `[data-index]` lookup lands on that
// hidden duplicate and `scrollIntoView` silently does nothing. When the
// redline is the visible view we instead find the block span containing each
// matched word and paint a character Range inside it (CSS Custom Highlight
// API, same highlight names as the translated path).

/** Scroll the nearest scrollable ancestor of `anchor` so `rect` is centered. */
function scrollRectToCenter(anchor: HTMLElement, rect: DOMRect): void {
  let scroller = anchor.parentElement;
  while (
    scroller &&
    !(
      scroller.scrollHeight > scroller.clientHeight &&
      /(auto|scroll)/.test(getComputedStyle(scroller).overflowY)
    )
  ) {
    scroller = scroller.parentElement;
  }
  if (!scroller) {
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  const scrollerRect = scroller.getBoundingClientRect();
  scroller.scrollBy({
    top:
      rect.top + rect.height / 2 - (scrollerRect.top + scrollerRect.height / 2),
    behavior: 'smooth',
  });
}

/**
 * Highlight and scroll to search matches in the redline view. Returns false
 * (doing nothing) when the redline isn't the visible view, so the caller
 * falls back to the per-word `data-index` path.
 */
export function applyRedlineSearchHighlights(
  container: HTMLElement,
  matches: SearchMatch[],
  activeIdx: number,
  elements: TranscriptElement[],
): boolean {
  const blockSpans = Array.from(
    container.querySelectorAll<HTMLElement>('[data-index-end]'),
  );
  // The diff stays mounted under `display: none` outside redline — only a
  // rendered block span means the redline is what the user is looking at.
  if (!blockSpans.some(span => span.getClientRects().length > 0)) return false;

  // Block spans are in document (= index) order; binary-search the one
  // whose [start, end) range holds `idx`.
  const starts = blockSpans.map(span => Number(span.dataset.index));
  const findBlock = (idx: number): HTMLElement | null => {
    let lo = 0;
    let hi = starts.length - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid] <= idx) {
        found = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (found < 0) return null;
    const span = blockSpans[found];
    return idx < Number(span.dataset.indexEnd) ? span : null;
  };

  // A Range over the match's words: from the first word's start to the last
  // word's end. Words in different blocks get one Range each.
  const rangesForMatch = (match: SearchMatch): Range[] => {
    const ranges: Range[] = [];
    for (const idx of match.indices) {
      const span = findBlock(idx);
      const value = elements[idx]?.value;
      if (!span || !value) continue;
      let charOffset = 0;
      for (let k = Number(span.dataset.index); k < idx; k++) {
        charOffset += elements[k]?.value?.length ?? 0;
      }
      const start = findTextNodeAtCharOffset(span, charOffset);
      const end = findTextNodeAtCharOffset(span, charOffset + value.length);
      if (!start || !end) continue;
      try {
        const range = new Range();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        ranges.push(range);
      } catch {
        // Invalid offsets (DOM changed under us) — skip, as the translated
        // path does; the match count stays accurate.
      }
    }
    return ranges;
  };

  const matchRanges: Range[] = [];
  let activeRanges: Range[] = [];
  matches.forEach((m, i) => {
    const ranges = rangesForMatch(m);
    if (i === activeIdx) activeRanges = ranges;
    else matchRanges.push(...ranges);
  });

  if (supportsHighlightApi()) {
    const cssH = CSS as unknown as CssWithHighlights;
    const Highlight = (window as unknown as WindowWithHighlight).Highlight;
    if (matchRanges.length > 0) {
      cssH.highlights.set(
        TRANSLATED_HIGHLIGHT_NAME,
        new Highlight(...matchRanges),
      );
    }
    if (activeRanges.length > 0) {
      cssH.highlights.set(
        TRANSLATED_HIGHLIGHT_ACTIVE_NAME,
        new Highlight(...activeRanges),
      );
    }
  }

  const active = matches[activeIdx];
  const activeSpan = active ? findBlock(active.startIndex) : null;
  if (activeSpan) {
    if (activeRanges.length > 0) {
      scrollRectToCenter(activeSpan, activeRanges[0].getBoundingClientRect());
    } else {
      activeSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
  return true;
}
